/**
 * Articles Swiper System
 * 
 * @description Dedicated swiper system for article carousels.
 * Handles specific logic for article sections with dynamic configuration.
 * 
 * @features
 * - Dynamic configuration based on article count
 * - Responsive breakpoints optimized for articles
 * - Secondary pagination support
 * - Fraction pagination
 * - Auto-initialization for article swipers
 * 
 * @dependencies Swiper.js library
 * @version 1.0.0
 */

window.ArticlesSwiper = (function() {
  
  // Base articles configuration
  const ARTICLES_CONFIG = {
    slidesPerView: 2.5,
    spaceBetween: 20,
    freeMode: {
      enabled: true,
      momentum: true,
      momentumRatio: 0.8
    },
    mousewheel: {
      releaseOnEdges: true,
      forceToAxis: true,
      sensitivity: 1
    },
    touchEventsTarget: 'container',
    grabCursor: true,
    breakpoints: {
      0: {
        slidesPerView: 1.5,
        slidesOffsetBefore: 20,
        slidesOffsetAfter: 20
      },
      769: {
        slidesPerView: 2.5,
        slidesOffsetBefore: 20,
        slidesOffsetAfter: 20
      },
      1025: {
        slidesPerView: 3.5,
        slidesOffsetBefore: 60,
        slidesOffsetAfter: 60
      },
      1440: {
        slidesPerView: 3.5,
        slidesOffsetBefore: 90,
        slidesOffsetAfter: 90
      },
    }
  };

  // Function to update fraction pagination
  function updateFractionPagination(swiper) {
    try {
      const paginationContainer = swiper.el.querySelector('.swiper-pagination-bullets');
      if (!paginationContainer) return;
      
      const bullets = paginationContainer.querySelectorAll('.swiper-pagination-bullet');
      const totalPages = bullets.length;
      let currentPage = 1;
      
      bullets.forEach((bullet, index) => {
        if (bullet.classList.contains('swiper-pagination-bullet-active')) {
          currentPage = index + 1;
        }
      });
      
      const fractionText = currentPage + ' / ' + totalPages;
      const fractionPagination = swiper.el.querySelector('.swiper-pagination-fraction');
      
      if (fractionPagination) {
        fractionPagination.textContent = fractionText;
      }
    } catch (error) {
      // Silently handle errors
    }
  }

  // Function to create and manage secondary pagination
  function createSecondaryPagination(swiper) {
    const swiperContainer = swiper.el;
    const primaryPagination = swiperContainer.querySelector('.swiper-pagination-bullets');
    const secondaryContainer = swiperContainer.querySelector('.swiper-pagination-bullets-secondary');
    
    if (!primaryPagination || !secondaryContainer) return;
    
    function updateSecondaryBullets() {
      const primaryBullets = primaryPagination.querySelectorAll('.swiper-pagination-bullet');
      
      if (primaryBullets.length === 0) {
        setTimeout(updateSecondaryBullets, 100);
        return;
      }
      
      // Clear secondary container
      secondaryContainer.innerHTML = '';
      
      // Create secondary bullets
      primaryBullets.forEach((bullet, index) => {
        const secondaryBullet = document.createElement('span');
        secondaryBullet.className = 'swiper-pagination-bullet';
        
        // Copy active state
        if (bullet.classList.contains('swiper-pagination-bullet-active')) {
          secondaryBullet.classList.add('swiper-pagination-bullet-active');
        }
        
        // Add click event
        secondaryBullet.addEventListener('click', () => {
          swiper.slideTo(index);
        });
        
        secondaryContainer.appendChild(secondaryBullet);
      });
    }
    
    // Update on swiper events
    swiper.on('init', updateSecondaryBullets);
    swiper.on('slideChange', updateSecondaryBullets);
    
    // Execute immediately if already initialized
    if (swiper.initialized) {
      updateSecondaryBullets();
    }
  }

  // Function to get configuration for articles swiper
  function getArticlesConfig(swiperElement) {
    const config = Object.assign({}, ARTICLES_CONFIG);
    
    // Standard configuration for all articles swipers
    return config;
  }

  // Function to initialize an articles swiper
  function initArticlesSwiper(swiperElement) {
    if (!swiperElement || typeof Swiper === 'undefined' || swiperElement.swiper) {
      return null;
    }

    // Verify slides exist before initializing
    const slides = swiperElement.querySelectorAll('.swiper-slide');
    if (slides.length === 0) {
      return null;
    }

    try {
      const config = getArticlesConfig(swiperElement);
      
      // Add navigation
      config.navigation = {
        nextEl: swiperElement.querySelector('.swiper-button-next'),
        prevEl: swiperElement.querySelector('.swiper-button-prev'),
      };
      
      // Add pagination
      config.pagination = {
        el: swiperElement.querySelector('.swiper-pagination-bullets'),
        type: 'bullets',
        clickable: true,
        renderBullet: function (index, className) {
          return `<span class="${className}"></span>`;
        },
      };
      
      // Add event handlers
      config.on = {
        init: function() {
          updateFractionPagination(this);
          
          // Create secondary pagination if container exists
          const secondaryContainer = this.el.querySelector('.swiper-pagination-bullets-secondary');
          if (secondaryContainer) {
            createSecondaryPagination(this);
          }
        },
        slideChange: function() {
          updateFractionPagination(this);
        }
      };

      return new Swiper(swiperElement, config);
    } catch (error) {
      return null;
    }
  }

  // Function to initialize all articles swipers
  function initAllArticlesSwipers() {
    const elements = document.querySelectorAll('.swiper-carousel[data-swiper-type="articles"]');
    const initializedSwipers = [];
    
    elements.forEach(element => {
      const swiper = initArticlesSwiper(element);
      if (swiper) {
        initializedSwipers.push(swiper);
      }
    });
    
    return initializedSwipers;
  }

  // Auto-initialization
  function autoInit() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initAllArticlesSwipers);
    } else {
      initAllArticlesSwipers();
    }
  }

  // Public API
  return {
    init: initAllArticlesSwipers,
    initSingle: initArticlesSwiper,
    autoInit: autoInit
  };
})();

// Auto-initialize
ArticlesSwiper.autoInit();